// src/pages/business/products/ProductJourney.tsx
// Web adaptation of mobile ProductJourneyScreen - Track product from creation to sale

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, TrendingUp, DollarSign, Wallet, BarChart3,
  Calendar, Package, Loader2, AlertCircle, ShoppingCart,
  ArrowUpRight, ArrowDownRight, Clock, Tag
} from 'lucide-react';
import apiClient from '@/services/apiClient';

interface ProductTransaction {
  id: string;
  date: string;
  quantity: number;
  price: number;
  total: number;
  profit: number;
}

interface DailySale {
  date: string;
  quantity: number;
  revenue: number;
}

interface ProductAnalytics {
  id: string;
  name: string;
  barcode: string;
  category: string;
  current_stock: number;
  cost_price: number;
  selling_price: number;
  first_sale_date: string | null;
  last_sale_date: string | null;
  total_sold: number;
  total_revenue: number;
  total_profit: number;
  profit_margin: number;
  sales_count: number;
  stock_turnover_rate: number;
  average_sale_value: number;
  peak_sales_day: string | null;
  transactions: ProductTransaction[];
  daily_sales: DailySale[];
}

const ProductJourney: React.FC = () => {
  const { businessId, productId } = useParams<{ businessId: string; productId: string }>();
  const navigate = useNavigate();

  const [analytics, setAnalytics] = useState<ProductAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchProductAnalytics();
  }, [businessId, productId]);

  const fetchProductAnalytics = async () => {
    try {
      setLoading(true);
      const { data } = await apiClient.get(`/businesses/${businessId}/products/${productId}/analytics`);
      setAnalytics(data);
    } catch (err: any) {
      console.error('Error fetching product analytics:', err);
      setError(err.response?.data?.error || 'Failed to load product analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `KES ${amount.toLocaleString('en-KE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 size={40} className="animate-spin" style={{ color: '#4F46E5' }} />
          <p className="text-sm text-light-subtle dark:text-dark-subtle">Loading product analytics...</p>
        </div>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center p-8">
          <AlertCircle size={48} style={{ color: '#EF4444' }} />
          <h2 className="text-xl font-bold text-light-text dark:text-dark-text">Failed to Load Analytics</h2>
          <p className="text-light-subtle dark:text-dark-subtle">{error || 'The requested data could not be loaded.'}</p>
          <button
            onClick={() => navigate(`/business/${businessId}/products`)}
            className="mt-4 px-6 py-3 rounded-xl text-white font-semibold transition-colors"
            style={{ backgroundColor: '#4F46E5' }}
          >
            Go Back to Products
          </button>
        </div>
      </div>
    );
  }

  // Simple chart component using div bars
  const SimpleLineChart = ({ data }: { data: DailySale[] }) => {
    const maxVal = Math.max(...data.map(d => d.quantity), 1);
    const last7 = data.slice(-7);

    return (
      <div className="flex items-end gap-2 h-40 mt-4">
        {last7.map((d, i) => {
          const height = (d.quantity / maxVal) * 100;
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full bg-primary/20 dark:bg-primary/30 rounded-t relative" style={{ height: `${height}%` }}>
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-semibold text-light-text dark:text-dark-text">
                  {d.quantity}
                </div>
              </div>
              <p className="text-xs text-light-subtle dark:text-dark-subtle">
                {new Date(d.date).getDate()}
              </p>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border">
        <div className="max-w-5xl mx-auto flex items-center gap-4 px-4 py-4">
          <button
            onClick={() => navigate(`/business/${businessId}/products/${productId}`)}
            className="p-2 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
          >
            <ArrowLeft size={24} style={{ color: '#4F46E5' }} />
          </button>
          <div>
            <h1 className="font-bold text-lg text-light-text dark:text-dark-text">{analytics.name}</h1>
            <p className="text-xs text-light-subtle dark:text-dark-subtle">{analytics.category}</p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(16,185,129,0.12)' }}>
                <TrendingUp size={16} style={{ color: '#10B981' }} />
              </div>
            </div>
            <p className="text-2xl font-bold text-light-text dark:text-dark-text">{analytics.total_sold}</p>
            <p className="text-xs text-light-subtle dark:text-dark-subtle">Units Sold</p>
          </div>

          <div className="bg-light-card dark:bg-dark-card rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(79,70,229,0.12)' }}>
                <DollarSign size={16} style={{ color: '#4F46E5' }} />
              </div>
            </div>
            <p className="text-xl font-bold text-light-text dark:text-dark-text">{formatCurrency(analytics.total_revenue)}</p>
            <p className="text-xs text-light-subtle dark:text-dark-subtle">Total Revenue</p>
          </div>

          <div className="bg-light-card dark:bg-dark-card rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(245,158,11,0.12)' }}>
                <Wallet size={16} style={{ color: '#F59E0B' }} />
              </div>
            </div>
            <p className="text-xl font-bold text-light-text dark:text-dark-text">{formatCurrency(analytics.total_profit)}</p>
            <p className="text-xs text-light-subtle dark:text-dark-subtle">Total Profit</p>
          </div>

          <div className="bg-light-card dark:bg-dark-card rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(16,185,129,0.12)' }}>
                <BarChart3 size={16} style={{ color: '#10B981' }} />
              </div>
            </div>
            <p className="text-2xl font-bold text-light-text dark:text-dark-text">{analytics.profit_margin.toFixed(1)}%</p>
            <p className="text-xs text-light-subtle dark:text-dark-subtle">Profit Margin</p>
          </div>
        </div>

        {/* Product Timeline */}
        <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm">
          <h3 className="font-semibold text-sm mb-4 text-light-text dark:text-dark-text flex items-center gap-2">
            <Clock size={16} />
            Product Timeline
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="p-3 bg-light-bg dark:bg-dark-bg rounded-lg">
              <p className="text-xs text-light-subtle dark:text-dark-subtle mb-1">First Sale</p>
              <p className="font-medium text-sm text-light-text dark:text-dark-text">
                {analytics.first_sale_date ? formatDate(analytics.first_sale_date) : 'No sales yet'}
              </p>
            </div>
            <div className="p-3 bg-light-bg dark:bg-dark-bg rounded-lg">
              <p className="text-xs text-light-subtle dark:text-dark-subtle mb-1">Last Sale</p>
              <p className="font-medium text-sm text-light-text dark:text-dark-text">
                {analytics.last_sale_date ? formatDate(analytics.last_sale_date) : 'N/A'}
              </p>
            </div>
            <div className="p-3 bg-light-bg dark:bg-dark-bg rounded-lg">
              <p className="text-xs text-light-subtle dark:text-dark-subtle mb-1">Peak Sales Day</p>
              <p className="font-medium text-sm text-light-text dark:text-dark-text">
                {analytics.peak_sales_day ? formatDate(analytics.peak_sales_day) : 'N/A'}
              </p>
            </div>
            <div className="p-3 bg-light-bg dark:bg-dark-bg rounded-lg">
              <p className="text-xs text-light-subtle dark:text-dark-subtle mb-1">Total Transactions</p>
              <p className="font-medium text-sm text-light-text dark:text-dark-text">{analytics.sales_count}</p>
            </div>
            <div className="p-3 bg-light-bg dark:bg-dark-bg rounded-lg">
              <p className="text-xs text-light-subtle dark:text-dark-subtle mb-1">Avg Sale Value</p>
              <p className="font-medium text-sm text-light-text dark:text-dark-text">{formatCurrency(analytics.average_sale_value)}</p>
            </div>
          </div>
        </div>

        {/* Sales Trend Chart */}
        {analytics.daily_sales && analytics.daily_sales.length > 0 && (
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm">
            <h3 className="font-semibold text-sm mb-4 text-light-text dark:text-dark-text">Sales Trend (Last 7 Days)</h3>
            <SimpleLineChart data={analytics.daily_sales} />
          </div>
        )}

        {/* Stock Information */}
        <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm">
          <h3 className="font-semibold text-sm mb-4 text-light-text dark:text-dark-text flex items-center gap-2">
            <Package size={16} />
            Current Stock Status
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 bg-light-bg dark:bg-dark-bg rounded-lg">
              <p className="text-xs text-light-subtle dark:text-dark-subtle mb-1">Stock Level</p>
              <p className={`font-bold text-lg ${
                analytics.current_stock < 10 
                  ? 'text-red-500' 
                  : analytics.current_stock < 25 
                  ? 'text-yellow-500' 
                  : 'text-green-500'
              }`}>
                {analytics.current_stock} units
              </p>
            </div>
            <div className="p-3 bg-light-bg dark:bg-dark-bg rounded-lg">
              <p className="text-xs text-light-subtle dark:text-dark-subtle mb-1">Turnover Rate</p>
              <p className="font-medium text-sm text-light-text dark:text-dark-text">{analytics.stock_turnover_rate.toFixed(2)}x</p>
            </div>
            <div className="p-3 bg-light-bg dark:bg-dark-bg rounded-lg">
              <p className="text-xs text-light-subtle dark:text-dark-subtle mb-1">Cost Price</p>
              <p className="font-medium text-sm text-light-text dark:text-dark-text">{formatCurrency(analytics.cost_price)}</p>
            </div>
            <div className="p-3 bg-light-bg dark:bg-dark-bg rounded-lg">
              <p className="text-xs text-light-subtle dark:text-dark-subtle mb-1">Selling Price</p>
              <p className="font-bold text-sm" style={{ color: '#4F46E5' }}>{formatCurrency(analytics.selling_price)}</p>
            </div>
          </div>
        </div>

        {/* Transaction History */}
        <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm">
          <h3 className="font-semibold text-sm mb-4 text-light-text dark:text-dark-text flex items-center gap-2">
            <ShoppingCart size={16} />
            Recent Transactions ({analytics.transactions.length})
          </h3>
          {analytics.transactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Tag size={48} className="text-light-subtle dark:text-dark-subtle" />
              <p className="text-sm text-light-subtle dark:text-dark-subtle">No transactions yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {analytics.transactions.slice(0, 10).map((transaction) => (
                <div key={transaction.id} className="flex items-center justify-between p-3 bg-light-bg dark:bg-dark-bg rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-sm text-light-text dark:text-dark-text">{formatDate(transaction.date)}</p>
                    <p className="text-xs text-light-subtle dark:text-dark-subtle">
                      {transaction.quantity} units @ {formatCurrency(transaction.price)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-sm text-light-text dark:text-dark-text">{formatCurrency(transaction.total)}</p>
                    <p className="text-xs text-green-500">+{formatCurrency(transaction.profit)} profit</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductJourney;